import React, { useState, useEffect } from "react";
import { StyleSheet, Platform, View } from "react-native";
import {
  NavigationContainer,
  DarkTheme,
  DefaultTheme,
  getStateFromPath as parseNavigationPath,
  getPathFromState as formatNavigationPath,
} from "@react-navigation/native";
import { GestureRoot as GestureHandlerRootView } from "@/components/GestureRoot";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { StatusBar } from "expo-status-bar";
import { useTheme } from "@/hooks/useTheme";

import * as SplashScreen from "expo-splash-screen";

import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";

import RootStackNavigator from "@/navigation/RootStackNavigator";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { migrateLegacyUser, seedModeFromTier } from "@/features/persona";
import { useBilling } from "@/hooks/useBilling";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { AlertProvider } from "@/context/AlertContext";
import { AmbientBlobs } from "@/components/AmbientBlobs";
import CheckoutResume from "@/components/CheckoutResume";
import { installWebDesignSystem, syncWebThemeAttribute } from "@/lib/webDesignSystem";
import { normalizeLegacyPath, stripNonUrlParams } from "@/lib/navigationPaths";

const NotificationBanner = React.lazy(() => import("@/components/NotificationBanner").then(module => ({ default: module.NotificationBanner })));

/** Keep optional live-alert work out of the first market reading on web. */
function useInteractiveBoot() {
  const [ready, setReady] = useState(Platform.OS !== "web");
  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    const activate = () => setReady(true);
    window.addEventListener("pointerdown", activate, { once: true, passive: true });
    window.addEventListener("keydown", activate, { once: true });
    return () => {
      window.removeEventListener("pointerdown", activate);
      window.removeEventListener("keydown", activate);
    };
  }, []);
  return ready;
}

function DeferredNotificationBanner() {
  const ready = useInteractiveBoot();
  if (!ready) return null;
  return <React.Suspense fallback={null}><NotificationBanner /></React.Suspense>;
}

SplashScreen.preventAutoHideAsync();

const linking: any = {
  // 🔴 `fahali://` FIRST, and it was MISSING (2026-08-30). `app.json` declares
  // `"scheme": "fahali"`, so that is the only scheme Android/iOS actually
  // register for this app — every `fahali://` deep link was arriving at a
  // navigator that did not recognise it. `spectra://` is the legacy scheme and
  // is kept only so links minted before the rename still resolve; nothing
  // registers it any more, so it can be dropped once none are in flight.
  prefixes: [
    "fahali://",
    "spectra://",
    "https://app.fahaliai.com",
    "https://spectra-api-server-production.up.railway.app",
  ],
  getStateFromPath: (path: string, options: unknown) =>
    parseNavigationPath(normalizeLegacyPath(path), options as any),
  // Object params are session context, never part of the address — without
  // this, `entryEvidence` was written as `?entryEvidence=[object Object]`.
  getPathFromState: (state: any, options: unknown) =>
    formatNavigationPath(stripNonUrlParams(state), options as any),
  config: {
    // 🔴 A screen opened from a link or a refresh was the ONLY entry in the
    // stack, so canGoBack() was false and every "Back" on it did nothing
    // (alerts, cases, analytics, volsurface measured dead 2026-10-01).
    // Seeding Main underneath makes Back return to the app from any entry.
    initialRouteName: "Main",
    screens: {
      Main: {
        initialRouteName: "Dashboard",
        screens: {
          Dashboard: "dashboard",
          Intelligence: "intelligence",
          Portfolio: "portfolio",
          Profile: "profile",
        },
      },
      // 🔴 Sign-in must have a URL. Without this entry `/auth` resolved to no
      // screen and React Navigation fell back to the initial route, so
      // app.fahaliai.com/auth silently landed on the dashboard — no way in by
      // link, from a marketing page, or from a password-manager entry. The
      // screen itself was fine and registered (RootStackNavigator gates it on
      // `!user || user.isDemo`); only the path was missing.
      Auth: "auth",
      // Public paths are lowercase and stable. normalizeLegacyPath preserves
      // already-shared links that used the old component-name URLs.
      // 🔴 This read `"symbol"` with no parameter, so `/symbol/BTCUSDT` matched
      // no screen and React Navigation fell back to the initial route — the
      // reader landed silently on the dashboard. IDENTICAL to the `/auth`
      // defect documented four lines below, and in the same file.
      //
      // Fahali's whole pitch is a read you can show someone. A symbol read with
      // no URL cannot be sent to a colleague, linked from a briefing, or
      // reopened from history — and `navigationPaths.ts:4` already normalises
      // `/Symbol/X` to `/symbol/X`, so shareable links were always intended.
      // The parameter is OPTIONAL so a bare `/symbol` still resolves; the
      // screen already reads `route.params?.symbol ?? ""`.
      Symbol: "symbol/:symbol?",
      AlertDetail: "alert/:alertId",
      ForecastPanel: "forecast/:symbol",
      ContagionMap: "contagion",
      TemporalArbitrage: "temporal",
      EngineDeepDive: "engine/:engineType",
      WatchSetup: "watch",
      Pricing: "pricing",
      Accounts: "accounts",
      Settings: "settings",
      Developer: "developer",
      ConnectHub: "connect",
      ExtensionConnect: "connect-extension",
      MarketHeatmap: "heatmap",
      DarkPool: "darkpool",
      FlashCrash: "flashcrash",
      IvRvTermStructure: "ivrv",
      AlphaAttribution: "alpha",
      CaseStudies: "cases",
      OcaTrace: "oca/:symbol?",
      SentimentTimeline: "sentiment",
      SymbolSearch: "search",
      Insight: "insights",
      Learn: "learn",
      CapitalFlow: "capital-flow",
      WatchlistV2: "watchlist",
      AlertsV2: "alerts",
      EngineExplorer: "market-checks",
      RiskMonitor: "risk-monitor",
      Analytics: "analytics",
      PortfolioCommand: "portfolio-analysis",
      DeepDive: "deep-dive",
      MonitoringRules: {
        path: "monitoring-rules",
        parse: {
          prefillValues: (value: string) => {
            try {
              return value && value !== "[object Object]" ? JSON.parse(value) : {};
            } catch {
              return {};
            }
          },
        },
        stringify: {
          prefillValues: (value: unknown) =>
            value && typeof value === "object" ? JSON.stringify(value) : "",
        },
      },
      // CorrelationMatrix screen removed - route not found
      VolatilitySurface: "volsurface",
    },
  },
};

function PersonaInit() {
  const { user } = useAuth();
  const { subscription } = useBilling();
  const tier = subscription?.tier;

  // Keep the starting experience centralized. All tiers currently begin in
  // Plain mode; readers who explicitly choose Professional retain
  // that preference. Subscription level controls access, not comprehension.
  useEffect(() => {
    seedModeFromTier(tier);
  }, [tier]);

  // 2. Legacy migration, kept for users who predate the persona system.
  //
  // Existing portfolio data no longer implies a preference for technical
  // language; migrateLegacyUser records Plain for these older accounts.
  useEffect(() => {
    if (!user) return;
    AsyncStorage.getItem("@fahali_portfolio").then((raw) => {
      const hasPortfolio = !!raw && raw !== "[]" && raw !== "{}";
      if (hasPortfolio) migrateLegacyUser(hasPortfolio);
    });
  }, [user]);

  return null;
}

export default function App() {
  const [fontsLoaded, setFontsLoaded] = useState(Platform.OS === "web");
  const [fontError, setFontError] = useState<Error | null>(null);

  useEffect(() => {
    async function loadFonts() {
      try {
        if (Platform.OS === 'web') {
          setFontsLoaded(true);
          return;
        }
        // Web serves Inter from the document head. Keeping native font modules
        // statically imported made a browser parse all 13 font assets despite
        // this branch never using them. Native still loads the exact same set.
        const [Font, outfit, inter, fraunces, mono] = await Promise.all([
          import("expo-font"),
          import("@expo-google-fonts/outfit"),
          import("@expo-google-fonts/inter"),
          import("@expo-google-fonts/fraunces"),
          import("@expo-google-fonts/jetbrains-mono"),
        ]);
        await Font.loadAsync({
          Outfit_400Regular: outfit.Outfit_400Regular,
          Outfit_500Medium: outfit.Outfit_500Medium,
          Outfit_600SemiBold: outfit.Outfit_600SemiBold,
          Outfit_700Bold: outfit.Outfit_700Bold,
          Inter_400Regular: inter.Inter_400Regular,
          Inter_500Medium: inter.Inter_500Medium,
          Inter_600SemiBold: inter.Inter_600SemiBold,
          Inter_700Bold: inter.Inter_700Bold,
          Fraunces_300Light_Italic: fraunces.Fraunces_300Light_Italic,
          Fraunces_400Regular: fraunces.Fraunces_400Regular,
          JetBrainsMono_400Regular: mono.JetBrainsMono_400Regular,
          JetBrainsMono_500Medium: mono.JetBrainsMono_500Medium,
          JetBrainsMono_600SemiBold: mono.JetBrainsMono_600SemiBold,
        });
        setFontsLoaded(true);
      } catch (e) {
        /* production: no log */
        setFontError(e as Error);
      }
    }
    loadFonts();
  }, []);

  useEffect(() => {
    if (fontsLoaded || fontError) {
      SplashScreen.hideAsync();
    }
  }, [fontsLoaded, fontError]);

  const { theme: appTheme, isDark, mode } = useTheme();
  const alertTransportReady = useInteractiveBoot();

  // Park Avenue global web styles: document background (so overscroll shows
  // the sand canvas rather than white), ambient keyframes, focus rings.
  useEffect(() => {
    installWebDesignSystem();
  }, []);

  useEffect(() => {
    syncWebThemeAttribute(mode);
  }, [mode]);

  if (!fontsLoaded && !fontError) {
    return null;
  }

  // Ambient wash: the three brief accents at low alpha. Steel Blue replaces
  // the previous neutral grey so the background has a cool counterweight to
  // the gold instead of reading as dust.
  const blobColors: [string, string, string] = [
    appTheme.riskRed,
    appTheme.primary,
    appTheme.steelBlue,
  ];

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SafeAreaProvider>
          <AuthProvider>
            <PersonaInit />
            <AlertProvider enabled={alertTransportReady}>
                <GestureHandlerRootView style={styles.root}>
                <AmbientBlobs colors={blobColors} />
                {/* A real desktop canvas: wide enough for portfolio-first
                    composition, bounded only on very large monitors. */}
                <View style={[styles.webShellOuter, { backgroundColor: appTheme.backgroundRoot }]}>
                <View style={[styles.webShellInner, Platform.OS === "web" && { maxWidth: 1440 }]}>
                <NavigationContainer
                  linking={linking}
                  // The browser tab read the literal string "undefined" (verified on
                  // production 2026-08-08). React Navigation's web layer writes
                  // document.title on every focus change, and its default formatter is
                  // `options?.title ?? route?.name` — with 39 of our 41 screens setting
                  // neither, that resolved to undefined and got stringified into the tab.
                  // The static shell's correct <title>Fahali</title> was overwritten at
                  // hydration, so the served HTML looked fine and the tab did not.
                  //
                  // Absence renders as a value again: the fix is an explicit fallback,
                  // not a library default. Deliberately keyed on options.title ONLY —
                  // route.name would leak internal identifiers into a user-visible tab.
                  documentTitle={{
                    formatter: (options) =>
                      options?.title ? `Fahali — ${options.title}` : "Fahali",
                  }}
                  theme={{
                    ...(isDark ? DarkTheme : DefaultTheme),
                    colors: {
                      ...(isDark ? DarkTheme : DefaultTheme).colors,
                      primary: appTheme.primary,
                      background: appTheme.backgroundRoot,
                      card: appTheme.backgroundElevated,
                      text: appTheme.textPrimary,
                      border: appTheme.hair,
                      notification: appTheme.primary,
                    }
                  }}
                >
                  <RootStackNavigator />
                  {/* The interrupt surface. Inside NavigationContainer so it
                      can navigate, and a sibling of the navigator so it
                      overlays every screen instead of sitting under a header.
                      Inside webShellInner so it follows the application canvas.
                      Renders null
                      unless something actually qualifies to interrupt. */}
                  <DeferredNotificationBanner />
                  {/* Carries a purchase across the sign-in detour. Same
                      placement and same reason as the banner above: inside the
                      container so it can navigate. Renders null; it only acts
                      when a real user appears with a fresh checkout intent. */}
                  <CheckoutResume />
                </NavigationContainer>
                </View>
                </View>
                <StatusBar style={isDark ? "light" : "dark"} />
                </GestureHandlerRootView>
            </AlertProvider>
          </AuthProvider>
        </SafeAreaProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  webShellOuter: {
    flex: 1,
    minHeight: 0,
    alignItems: "center",
    overflow: "hidden",
  },
  webShellInner: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    overflow: "hidden",
  },
  });
